// Price normalization utilities
// Centralized functions to handle price tick normalization across the application

import marketdata from "../data/Services/marketdata.jsx";
import { formatNumber } from "./numberFormatting.js";

/**
 * Get the minimum tick increment for a symbol
 * @param {string} symbol - The trading symbol
 * @returns {number} - The minimum price increment (default: 0.01)
 */
export const getTickSize = (symbol) => {
  if (!symbol) return 0.01;
  return marketdata?.getMinInc?.(symbol) || 0.01;
};

/**
 * Normalize a price to the nearest valid tick increment for a symbol
 * Uses the same logic as router.adjustMinPriceTick but as a standalone utility
 * @param {string} symbol - The trading symbol
 * @param {number} price - The raw price to normalize
 * @returns {number} - The normalized price
 */
export const normalizePrice = (symbol, price) => {
  if (!symbol || !price || !isFinite(price)) return price;

  const tickSize = getTickSize(symbol);

  // Integer-based arithmetic avoids IEEE 754 accumulation errors.
  // Instead of  Math.round(price / tickSize) * tickSize  (divide-then-multiply),
  // we use  Math.round(price * multiplier) / multiplier  where multiplier = 1/tickSize.
  // This keeps intermediate values in integer-friendly ranges (e.g. 5.68*100=568).
  // Only safe when 1/tickSize is an integer (0.01→100, 0.05→20, 0.1→10, 0.5→2).
  if (tickSize >= 1) {
    return Math.round(price / tickSize) * tickSize;
  } else {
    const rawMultiplier = 1 / tickSize;
    const roundedMultiplier = Math.round(rawMultiplier);
    if (Math.abs(rawMultiplier - roundedMultiplier) < 0.001) {
      return Math.round(price * roundedMultiplier) / roundedMultiplier;
    }
    // Fallback for non-standard tick sizes (e.g. 0.03) where 1/tick is not integer
    return Math.round(price / tickSize) * tickSize;
  }
};

/**
 * Calculate price offset by number of ticks
 * @param {string} symbol - The trading symbol
 * @param {number} basePrice - The base price to offset from
 * @param {number} tickCount - Number of ticks to offset (positive or negative)
 * @returns {number} - The offset price
 */
export const offsetByTicks = (symbol, basePrice, tickCount) => {
  if (!symbol || !isFinite(basePrice) || !isFinite(tickCount)) return basePrice;

  const tickSize = getTickSize(symbol);
  const offset = tickSize * tickCount;
  return normalizePrice(symbol, basePrice + offset);
};

/**
 * Compute number of ticks between two prices (target - base) / tickSize, rounded to nearest integer.
 * @param {string} symbol
 * @param {number} basePrice
 * @param {number} targetPrice
 * @returns {number|undefined} integer tick distance or undefined if invalid inputs
 */
export const computeTicksBetween = (symbol, basePrice, targetPrice) => {
  if (!symbol || !isFinite(basePrice) || !isFinite(targetPrice))
    return undefined;
  const tickSize = getTickSize(symbol);
  if (!tickSize) return undefined;
  const raw = (targetPrice - basePrice) / tickSize;
  // Round to nearest integer then ensure resulting price matches normalization when reapplied
  const ticks = Math.round(raw);
  return ticks;
};

/**
 * Compute R$ distance between two prices for a given quantity and symbol.
 * Uses FOQ (contract multiplier) from marketdata for derivatives.
 * @param {string} symbol
 * @param {number} basePrice
 * @param {number} targetPrice
 * @param {number} quantity
 * @returns {number|undefined} signed R$ value or undefined if invalid inputs
 */
export const computeReaisDistance = (
  symbol,
  basePrice,
  targetPrice,
  quantity,
) => {
  if (!symbol || !isFinite(basePrice) || !isFinite(targetPrice) || !quantity)
    return undefined;
  const priceDiff = targetPrice - basePrice;
  // FOQ do servidor (getFOQ, autoridade única). Indisponível → undefined: os
  // call sites já omitem o trecho "R$" quando não há valor — melhor que exibir
  // dinheiro calculado com multiplicador 1 num derivativo (erro de 5x a 50x).
  const foq = marketdata?.getFOQ?.(symbol);
  if (foq === null || foq === undefined) return undefined;
  return priceDiff * quantity * foq;
};

/**
 * Compute percentage distance between two prices.
 * @param {number} basePrice
 * @param {number} targetPrice
 * @returns {number|undefined} percentage value or undefined if invalid inputs
 */
export const computePercentDistance = (basePrice, targetPrice) => {
  if (!isFinite(basePrice) || !isFinite(targetPrice) || basePrice === 0)
    return undefined;
  return ((targetPrice - basePrice) / basePrice) * 100;
};

/**
 * Check if a price is on a valid tick boundary for a symbol
 * @param {string} symbol - The trading symbol
 * @param {number} price - The price to validate
 * @returns {boolean} - True if price is on valid tick boundary
 */
export const isValidTickPrice = (symbol, price) => {
  if (!symbol || !isFinite(price)) return false;

  const normalized = normalizePrice(symbol, price);
  return Math.abs(normalized - price) < 0.0000001; // Account for floating point precision
};

/**
 * Format price for display with appropriate decimal places based on tick size
 * Includes thousand separators for better readability using global locale settings
 * @param {string} symbol - The trading symbol
 * @param {number} price - The price to format
 * @returns {string} - Formatted price string with thousand separators
 */
export const formatPrice = (symbol, price) => {
  if (!isFinite(price)) return formatNumber(0, 2);

  const tickSize = getTickSize(symbol);

  // Determine decimal places based on tick size
  let decimalPlaces = 2;
  if (tickSize >= 1) {
    decimalPlaces = 0;
  } else if (tickSize >= 0.1) {
    decimalPlaces = 1;
  } else if (tickSize >= 0.01) {
    decimalPlaces = 2;
  } else if (tickSize >= 0.001) {
    decimalPlaces = 3;
  } else {
    decimalPlaces = 4;
  }

  // Use global number formatting with determined decimal places
  return formatNumber(price, decimalPlaces);
};
